import { CONSTANTS } from "@/lib/constants";

export type FieldDataType = "TEXT" | "JSON" | "INT" | "FLOAT" | "BOOLEAN" | "DATETIME";

export type RelationType = "OneToOne" | "OneToMany" | "ManyToOne" | "ManyToMany";

export interface FieldSpec {
  name: string;
  data_type: FieldDataType;
}

/** Either a platform entity addressed by its stable uuid, or another role of this feature. */
export type RelationTarget = { entityId: string } | { role: string };

export interface RelationSpec {
  field_name: string;
  type: RelationType;
  target: RelationTarget;
  /** Inverse field on the target; "" means the backend generates no inverse. */
  target_field_name: string;
}

export interface EntitySpec {
  /**
   * Stable key the feature refers to this entity by. Nothing outside this file
   * addresses an entity by name — resolution goes through the role — so a tenant
   * can later rename it, or bind the role to an entity it already had, without
   * any feature code changing.
   */
  role: string;
  defaultName: string;
  fields?: Array<FieldSpec>;
  relations?: Array<RelationSpec>;
}

export interface RecordRowSpec {
  values?: Record<string, unknown>;
  /** Relation field name -> the row of another role it should point at. */
  connect?: Record<string, { role: string; matchOn: string; value: unknown }>;
}

export interface RecordSpec {
  /**
   * Seed rows, written only when the entity has no rows at all. Repairing a
   * tenant's schema must never inject rows next to data it already curates —
   * its "Deutsch" is not our "German".
   */
  role: string;
  rows: Array<RecordRowSpec>;
}

/**
 * A relation a feature hangs on an entity it does not own — the feature
 * registering itself. It exists only for tenants that enabled the feature, so an
 * opt-in feature never forces its relation onto everyone else.
 */
export interface ContributionSpec {
  /** Role of the host entity, owned by another feature. */
  role: string;
  relation: RelationSpec;
}

export interface FeatureSpec {
  key: string;
  name: string;
  description?: string;
  /** Opt-in: excluded from the "all features present" gate and from "Activate all". */
  optional?: boolean;
  /** Role whose entity represents the feature in the UI. */
  primaryRole: string;
  /** Feature keys reconciled before this one. */
  dependsOn?: Array<string>;
  /** Entities this feature owns and creates. */
  entities: Array<EntitySpec>;
  contributes?: Array<ContributionSpec>;
  records?: Array<RecordSpec>;
}

/** Pins `key` to a string literal so `FeatureName` stays a union of the real keys. */
const defineFeature = <K extends string>(
  feature: FeatureSpec & { key: K },
): FeatureSpec & {
  key: K;
} => feature;

export const featureSpecs = [
  defineFeature({
    key: "CustomerConfig",
    name: "Customer Config",
    description:
      "Tenant-wide configuration. Holds no settings of its own — other features register theirs on it.",
    primaryRole: "CustomerConfig",
    entities: [{ role: "CustomerConfig", defaultName: "CustomerConfig" }],
  }),
  defineFeature({
    key: "UserProfile",
    name: "User Profile",
    description:
      "A basic user entity that is used to display certain properties like firstname and lastname in the admin dashboard",
    primaryRole: "UserProfile",
    entities: [
      {
        role: "UserProfile",
        defaultName: "UserProfile",
        fields: [
          { name: "firstname", data_type: "TEXT" },
          { name: "lastname", data_type: "TEXT" },
        ],
        relations: [
          {
            field_name: "pylo_user",
            type: "OneToOne",
            target: { entityId: CONSTANTS.ENTITY_UIDS.PYLO_USER },
            target_field_name: "user_profile",
          },
        ],
      },
    ],
  }),
  defineFeature({
    key: "MainLayout",
    name: "Main Layout",
    description:
      "The entity that holds the information about the users layout, like what to display, where to display, how to display etc.",
    primaryRole: "MainLayout",
    entities: [
      {
        role: "MainLayout",
        defaultName: "MainLayout",
        fields: [
          { name: "name", data_type: "TEXT" },
          { name: "folder_structure", data_type: "TEXT" },
        ],
      },
    ],
    records: [
      {
        role: "MainLayout",
        rows: [{ values: { name: "main-layout", folder_structure: "[]" } }],
      },
    ],
  }),
  defineFeature({
    key: "Language",
    name: "Language",
    optional: true,
    description: "The available languages used to translated entity fields",
    primaryRole: "Language",
    dependsOn: ["CustomerConfig"],
    entities: [
      {
        role: "Language",
        defaultName: "Language",
        fields: [
          { name: "name", data_type: "TEXT" },
          { name: "locale", data_type: "TEXT" },
        ],
      },
    ],
    contributes: [
      {
        role: "CustomerConfig",
        relation: {
          field_name: "default_language",
          type: "OneToOne",
          target: { role: "Language" },
          target_field_name: "",
        },
      },
    ],
    records: [
      {
        /** language codes: http://www.lingoes.net/en/translator/langcode.htm */
        role: "Language",
        rows: [
          { values: { name: "English", locale: "en" } },
          { values: { name: "German", locale: "de" } },
        ],
      },
      {
        role: "CustomerConfig",
        rows: [
          { connect: { default_language: { role: "Language", matchOn: "locale", value: "de" } } },
        ],
      },
    ],
  }),
  defineFeature({
    key: "DashboardView",
    name: "Dashboard View",
    description:
      "The entity that holds the information about the users layout, like what to display, where to display, how to display etc.",
    primaryRole: "DashboardView",
    entities: [
      {
        role: "DashboardView",
        defaultName: "DashboardView",
        fields: [
          { name: "name", data_type: "TEXT" },
          { name: "description", data_type: "TEXT" },
          { name: "filter", data_type: "JSON" },
          { name: "column_state", data_type: "JSON" },
          { name: "index", data_type: "INT" },
        ],
        relations: [
          {
            field_name: "entity",
            type: "ManyToOne",
            target: { entityId: CONSTANTS.ENTITY_UIDS.ENTITY },
            target_field_name: "",
          },
          {
            field_name: "pylo_aros",
            type: "ManyToMany",
            target: { entityId: CONSTANTS.ENTITY_UIDS.PYLO_ARO },
            target_field_name: "",
          },
        ],
      },
    ],
  }),
  defineFeature({
    key: "EntityView",
    name: "Entity View",
    description: "The entity that holds the information about the views of an entity",
    primaryRole: "EntityView",
    entities: [
      {
        role: "EntityView",
        defaultName: "EntityView",
        fields: [
          { name: "name", data_type: "TEXT" },
          { name: "description", data_type: "TEXT" },
          { name: "section_config", data_type: "JSON" },
          { name: "condition", data_type: "JSON" },
        ],
        relations: [
          {
            field_name: "entity",
            type: "ManyToOne",
            target: { entityId: CONSTANTS.ENTITY_UIDS.ENTITY },
            target_field_name: "entity_views",
          },
          {
            field_name: "pylo_aros",
            type: "ManyToMany",
            target: { entityId: CONSTANTS.ENTITY_UIDS.PYLO_ARO },
            target_field_name: "",
          },
        ],
      },
    ],
  }),
  defineFeature({
    key: "FlowNodeMeta",
    name: "Flow Node Meta",
    description: "Metadata for flow nodes including position information",
    primaryRole: "FlowNodeMeta",
    entities: [
      {
        role: "FlowNodeMeta",
        defaultName: "FlowNodeMeta",
        fields: [
          { name: "position_x", data_type: "FLOAT" },
          { name: "position_y", data_type: "FLOAT" },
        ],
        relations: [
          {
            field_name: "flow_node",
            type: "OneToOne",
            target: { entityId: CONSTANTS.ENTITY_UIDS.PYLO_FLOW_NODE },
            target_field_name: "flow_node_meta",
          },
        ],
      },
    ],
  }),
  defineFeature({
    key: "EntityViewComponentMapping",
    name: "Entity View Component Mapping",
    description: "Enables you to make relations look like you want to",
    primaryRole: "EntityViewComponentMapping",
    entities: [
      {
        role: "EntityViewComponentMapping",
        defaultName: "EntityViewComponentMapping",
        fields: [
          { name: "component", data_type: "TEXT" },
          { name: "config", data_type: "JSON" },
        ],
        relations: [
          {
            field_name: "entity",
            type: "OneToOne",
            target: { entityId: CONSTANTS.ENTITY_UIDS.ENTITY },
            target_field_name: "view_component",
          },
        ],
      },
    ],
  }),
  defineFeature({
    key: "Dashboard",
    name: "Dashboard",
    optional: true,
    description:
      "Persist saved dashboards (layout, widgets and date range) on the server so they survive across devices and are shared within your organization.",
    primaryRole: "Dashboard",
    entities: [
      {
        role: "Dashboard",
        defaultName: "Dashboard",
        fields: [
          { name: "name", data_type: "TEXT" },
          { name: "description", data_type: "TEXT" },
          { name: "config", data_type: "JSON" },
          { name: "cached_results", data_type: "JSON" },
        ],
      },
    ],
  }),
];

/** Union of every feature key, derived from the list above. */
export type FeatureName = (typeof featureSpecs)[number]["key"];

/** Roles are global: exactly one feature owns each, and any feature may reference it. */
export const entitySpecByRole = new Map<string, EntitySpec>(
  featureSpecs.flatMap((feature) => feature.entities).map((entity) => [entity.role, entity]),
);
